import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from './../../../../Common/Loading'
import { Search } from './../../../../Common/Tables/Search'

//import { InstructorBase } from './../../../../Layouts/Instructor/InstructorBase'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { LogErrors } from './../../../../../utilities/LogErrors'

import { GetAllDataGroups as BLGetAllDataGroups, GetZtudentsOfGroup as BLGetZtudentsOfGroup } from './../../../../../utilities/BL/Instructor/Groups'
import { GetOtherGroupsByInstructor as BLGetOtherGroupsByInstructor, MoveZtudentBetweenGroups as BLMoveZtudentBetweenGroups } from './../../../../../utilities/BL/Instructor/ChangeGroup'

import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'

import { Confirm } from './../../../../Common/Modals/Confirm'
import { Details } from './../../../../Common/Modals/Details'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from '../../../../../utilities/EL/WebPages'

//import { HELP_TYPE } from './../../../../Header/Header'
//import { useTawkChat } from './../../../../Support/Chat/Chat'



/**
 * Normaliza el response de grupos (visibles y no visibles) a un solo arreglo.
 * Estructura esperada (según tus otros componentes):
 *  - response.data.visibles: [{ idGroup, groupName, groupCode, ztudiantesCount, ... }]
 *  - response.data.noVisibles: mismo shape
 */
const normalizeGroups = (resp) => {
  if (!resp?.success) return []
  return [
    ...(resp?.data?.visibles ?? []).map(x => ({ ...x, visible: true })),
    ...(resp?.data?.noVisibles ?? []).map(x => ({ ...x, visible: false })),
  ]
}

const actions = {
  MOVE: 'MOVE'
}

const ChangeGroup = () => {
  const { showMessage } = useFloatMessage()
  const navigate = useNavigate()

  // Estado general
  const [isLoading, setIsLoading] = useState(true)   // carga inicial de la página
  const [isWorking, setIsWorking] = useState(false) // acciones en curso (cargar tabla / mover)
  const [errorMessage, setErrorMessage] = useState(null)

  // Grupos de origen (de donde se mueve)
  const [groupsFrom, setGroupsFrom] = useState([])
  const [selectedGroupFromId, setSelectedGroupFromId] = useState(0)
  const [isGroupsFromLoading, setIsGroupsFromLoading] = useState(false)

  // Grupos destino (a donde se mueve)
  const [groupsTo, setGroupsTo] = useState([])
  const [selectedGroupToId, setSelectedGroupToId] = useState(0)
  const [isGroupsToLoading, setIsGroupsToLoading] = useState(false)

  // Tabla de Ztudiantes (de grupo origen)
  const [allRows, setAllRows] = useState([])            // listado completo
  const [searchText, setSearchText] = useState('')      // texto del buscador
  const [selectedIds, setSelectedIds] = useState([])    // ids seleccionados para acción masiva

  const allRowsRef = useRef([])
  useEffect(() => {
    allRowsRef.current = allRows
  }, [allRows])

  // Modal de confirmación para acción individual
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [ztudentConfirmDetails, setZtudentConfirmDetails] = useState(null)
  const [actionTypeOfModal, setActionTypeOfModal] = useState(null)

  // Modal de resumen para acción masiva
  const [massiveSummary, setMassiveSummary] = useState(null)
  const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)

  // -------------------------
  // BÚSQUEDA + TABLA
  // -------------------------

  /**
   * Filtra el listado de Ztudiantes según el texto de búsqueda.
   * NOTA: la selección se mantiene porque se basa en idZtudiante.
   */
  const processedRows = useMemo(() => {
    const text = (searchText ?? '').toLowerCase()
    if (!text) return allRows

    return allRows.filter(r => {
      const fields = [
        `${r?.name ?? ''} ${r?.lastName ?? ''}`,
        r?.user
      ]
      return fields.some(v => String(v ?? '').toLowerCase().includes(text))
    })
  }, [allRows, searchText])

  const isRowSelected = (idZtudiante) => selectedIds.includes(idZtudiante)

  const toggleRowSelect = (idZtudiante) => {
    setSelectedIds(prev =>
      prev.includes(idZtudiante)
        ? prev.filter(id => id !== idZtudiante)
        : [...prev, idZtudiante]
    )
  }

  const clearSelections = () => {
    setSelectedIds([])
  }

  /**
   * Devuelve la info completa de los Ztudiantes seleccionados
   * usando allRowsRef (por si el filtrado modificó las vistas).
   */
  const getSelectedZtudentsByIds = (ids) => {
    return ids
      .map(id => allRowsRef.current.find(z => z.idZtudiante === id))
      .filter(Boolean)
  }

  // -------------------------
  // CARGA INICIAL
  // -------------------------

  /**
   * Carga inicial de la página:
   * - Obtiene el listado de grupos (origen).
   * - La tabla de Ztudiantes se muestra vacía hasta que se seleccione
   *   un grupo y se presione "Mostrar Ztudiantes".
   *
   * Importante:
   * - Ya no seteamos errorMessage aquí para evitar mostrar mensaje rojo al entrar.
   */
  const LoadPage = async () => {
    try {
      setIsLoading(true)
      setIsWorking(true)
      setIsGroupsFromLoading(true)
      setErrorMessage(null)

      const response = await BLGetAllDataGroups()
      if (response?.success) {
        const groups = normalizeGroups(response)
        setGroupsFrom(groups)
      } else {
        // Solo se registra en logs; no se muestra mensaje rojo al usuario en la carga inicial.
        await LogErrors('LoadPage - ChangeGroup (response not success)', response)
      }
    } catch (error) {
      await LogErrors('LoadPage - ChangeGroup', error)
      // No seteamos errorMessage para no mostrar mensaje de error al inicio.
    } finally {
      setIsGroupsFromLoading(false)
      setIsWorking(false)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    LoadPage()
  }, [])

  // -------------------------
  // MANEJO DE SELECTS DE GRUPOS
  // -------------------------

  /**
   * Cuando se selecciona un grupo de ORIGEN:
   * - Se limpia tabla y búsqueda.
   * - Se resetea el grupo destino seleccionado.
   * - Se limpia listado de grupos destino (para forzar recarga al mostrar Ztudiantes).
   */
  const handleSelectGroupFrom = (groupId) => {
    const id = parseInt(groupId, 10) || 0
    setSelectedGroupFromId(id)

    // Limpiar tabla y filtros
    setAllRows([])
    setSearchText('')
    clearSelections()

    // Reset de grupos destino
    setGroupsTo([])
    setSelectedGroupToId(0)
  }

  /**
   * Cuando se selecciona el grupo DESTINO a donde se moverán los Ztudiantes.
   */
  const handleSelectGroupTo = (groupId) => {
    const id = parseInt(groupId, 10) || 0
    setSelectedGroupToId(id)
  }

  /**
   * Carga el listado de grupos destino a partir del grupo origen seleccionado.
   * Usa BLGetOtherGroupsByInstructor(selectedGroupFromId).
   */
  const LoadGroupsToMoveList = async (groupFromId) => {
    try {
      setIsGroupsToLoading(true)

      const response = await BLGetOtherGroupsByInstructor(groupFromId)
      if (response?.success) {
        // Solo se utilizarán los grupos visibles. 
        const visibles = (response?.data?.visibles ?? []).map(g => ({
          idGroup: g.idGroup,
          groupName: g.groupName,
          groupCode: g.groupCode,
          ztudiantesQuantity: g.ztudiantesCount,
          visible: true
        }))

        setGroupsTo(visibles)
      } else {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: response?.message ?? 'No fue posible cargar el listado de grupos destino.'
        })
      }
    } catch (error) {
      await LogErrors('LoadGroupsToMoveList - ChangeGroup', error)
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar cargar el listado de grupos destino.'
      })
    } finally {
      setIsGroupsToLoading(false)
    }
  }

  // -------------------------
  // CARGAR ZTUDIANTES DEL GRUPO ORIGEN
  // -------------------------

  /**
   * Obtiene el listado de Ztudiantes del grupo de origen seleccionado
   * y los imprime en la tabla.
   */
  const LoadZtudentsTable = async () => {
    try {
      if (!selectedGroupFromId || selectedGroupFromId === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona primero un grupo de origen.'
        })
        return
      }

      setIsWorking(true)
      setErrorMessage(null)
      setAllRows([])
      setSearchText('')
      clearSelections()

      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: 'Cargando listado...'
      })

      const groupData = { id: selectedGroupFromId }
      const response = await BLGetZtudentsOfGroup(groupData)

      if (response?.success) {
        // Normalizar respuesta (como en Progress)
        const students = Array.isArray(response.data)
          ? response.data
          : (response.data?.ztudiantes ?? [])

        const rows = (students ?? []).map(s => ({
          idZtudiante: s.idZtudiante ?? s.id ?? s.studentId,
          name: s.name ?? '',
          lastName: s.lastName ?? '',
          user: s.user ?? s.email ?? ''
        }))

        setAllRows(rows)

        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: 'Listado cargado con éxito'
        })

        // Cargar listado de grupos destino basados en el grupo origen
        await LoadGroupsToMoveList(selectedGroupFromId)
      } else {
        setAllRows([])
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: response?.message ?? 'No ha sido posible obtener el listado de Ztudiantes.'
        })
      }
    } catch (error) {
      await LogErrors('LoadZtudentsTable - ChangeGroup', error)
      setAllRows([])
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar obtener el listado de Ztudiantes.'
      })
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // ACCIÓN INDIVIDUAL: MOVER UN ZTUDIANTe
  // -------------------------

  /**
   * Muestra el modal de confirmación para mover un solo Ztudiante.
   * NOTA: aquí SÍ se requerirá que ya exista grupo destino seleccionado.
   */
  const ShowConfirmMoveModal = (ztudent) => {
    if (!ztudent) return

    if (!selectedGroupToId || selectedGroupToId === 0) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Selecciona primero el grupo al que deseas mover al Ztudiante.'
      })
      return
    }

    setActionTypeOfModal(actions.MOVE)
    setZtudentConfirmDetails(ztudent)
    setShowConfirmationModal(true)
  }

  /**
   * Ejecuta el movimiento de un solo Ztudiante.
   * - Muestra solo notificación de éxito/error (NO modal de resumen).
   * - Actualiza la tabla eliminando al Ztudiante movido.
   */
  const MoveSingleZtudent = async () => {
    try {
      if (!ztudentConfirmDetails) return

      setIsWorking(true)

      const response = await BLMoveZtudentBetweenGroups(
        ztudentConfirmDetails,
        selectedGroupFromId,
        selectedGroupToId
      )

      const originGroup = groupsFrom.find(g => g.idGroup === selectedGroupFromId)
      const destGroup = groupsTo.find(g => g.idGroup === selectedGroupToId)

      if (response?.success) {
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `Se ha cambiado al Ztudiante '${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName}' del grupo '${originGroup?.groupName ?? ''}' al grupo '${destGroup?.groupName ?? ''}' de manera exitosa.`
        })

        // Eliminar al Ztudiante movido de la tabla
        const updatedRows = allRowsRef.current.filter(
          z => z.idZtudiante !== ztudentConfirmDetails.idZtudiante
        )
        setAllRows(updatedRows)
      } else {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: response?.message ?? 'No fue posible mover al Ztudiante.'
        })
      }

      // Importante: devolver la respuesta para que el componente Confirm
      // pueda cerrar el modal, igual que en Ztudents.
      return response
    } catch (error) {
      await LogErrors('MoveSingleZtudent - ChangeGroup', error)
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar mover al Ztudiante.'
      })
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // ACCIÓN MASIVA: MOVER VARIOS ZTUDIANTES
  // -------------------------

  /**
   * Maneja la acción masiva de mover uno o varios Ztudiantes al mismo grupo destino.
   * - Recorre cada Ztudiante seleccionado.
   * - Llama a BLMoveZtudentBetweenGroups individualmente.
   * - Construye un resumen con:
   *    - successful: Ztudiantes movidos correctamente.
   *    - wrongs: Ztudiantes que fallaron (incluyendo mensaje de error).
   *    - omitteds: Ztudiantes omitidos (por ejemplo, ya no existen en la tabla).
   * - Actualiza la tabla eliminando solo a los movidos correctamente.
   * - Muestra un modal de detalle con el resumen.
   */
  const HandleMassiveMoveZtudents = async () => {
    try {
      if (!selectedGroupFromId || selectedGroupFromId === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona primero el grupo de origen.'
        })
        return
      }

      if (!selectedGroupToId || selectedGroupToId === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona el grupo al que deseas mover a los Ztudiantes.'
        })
        return
      }

      if (!selectedIds || selectedIds.length === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona al menos un Ztudiante para mover.'
        })
        return
      }

      setIsWorking(true)
      setErrorMessage(null)

      const selectedZtudents = getSelectedZtudentsByIds(selectedIds)
      const resumen = {
        successful: [],
        wrongs: [],
        omitteds: []
      }

      let currentRows = [...allRowsRef.current]

      for (const ztudent of selectedZtudents) {
        try {
          // Verificar que el Ztudiante aún exista en la tabla actual
          const stillExists = currentRows.find(z => z.idZtudiante === ztudent.idZtudiante)
          if (!stillExists) {
            resumen.omitteds.push({
              ...ztudent,
              reason: 'Ya no existe en el listado'
            })
            continue
          }

          const response = await BLMoveZtudentBetweenGroups(
            ztudent,
            selectedGroupFromId,
            selectedGroupToId
          )

          if (response?.success) {
            resumen.successful.push(ztudent)
            // Eliminar de la tabla local
            currentRows = currentRows.filter(z => z.idZtudiante !== ztudent.idZtudiante)
          } else {
            resumen.wrongs.push({
              ...ztudent,
              error: response?.message ?? 'Error desconocido al mover al Ztudiante.'
            })
          }
        } catch (error) {
          resumen.wrongs.push({
            ...ztudent,
            error: error.message ?? 'Error interno al mover al Ztudiante.'
          })
        }
      }

      // Actualizar tabla con los que NO fueron movidos exitosamente
      setAllRows(currentRows)

      // Limpiar selección
      clearSelections()

      // Mostrar resumen en modal de Detalles
      setMassiveSummary(resumen)
      setActionTypeOfModal(actions.MOVE)
      setShowMassiveSummaryModal(true)
    } catch (error) {
      await LogErrors('HandleMassiveMoveZtudents - ChangeGroup', error)
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar mover a los Ztudiantes.'
      })
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // RENDER
  // -------------------------


  //useTawkChat(true) // Activamos el chat en esta página


  return (
    isLoading ? (
      <Loading />
    ) : (
      <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.INSTRUCTOR.CHANGE_GROUP} page={WEBPAGES.INSTRUCTOR.CHANGE_GROUP}>
        {/* Overlay de carga mientras se ejecuta una acción (mover uno o varios, cargar tabla, etc.) */}
        {isWorking && <Loading />}

        <section className='container Instructor__Reports'>
          <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
            Cambiar Ztudiante de grupo
          </p>

            <p className='text-center mt-4'>Selecciona un grupo:</p>
          {/* Filtros: grupo origen + botón para mostrar Ztudiantes */}
          <div className='Instructor__ChangeGroup__Reports__Filters'>
            {/* Listado de grupos (Origen o actual) */}
            {isGroupsFromLoading ? (
              <select className='Instructor__ChangeGroup__Reports__Filters__List' disabled={true}>
                <option>Cargando lista...</option>
              </select>
            ) : (
              <select
                className='Instructor__ChangeGroup__Reports__Filters__List'
                value={selectedGroupFromId}
                onChange={(e) => handleSelectGroupFrom(e.target.value)}
                disabled={isWorking || groupsFrom.length === 0}
              >
                <option value={0}>Selecciona un grupo</option>
                {groupsFrom.map(g => (
                  <option key={g.idGroup} value={g.idGroup}>
                    {g.groupName}
                  </option>
                ))}
              </select>
            )}

            <button
              type='button'
              className='Btn__Ztudia--PrimaryOutline Instructor__ChangeGroup__Reports__Filters__Button'
              onClick={LoadZtudentsTable}
              disabled={isWorking || !selectedGroupFromId || selectedGroupFromId === 0}
            >
              Mostrar Ztudiantes
            </button>
          </div>

          {/* Listado de grupos a los que se puede mover */}
          <p className='text-center mt-4'>¿A qué grupo deseas moverlo(s)?</p>
          <div className='Instructor__ChangeGroup__Reports__Filters'>
            {isGroupsToLoading ? (
              <select className='Instructor__ChangeGroup__Reports__Filters__List' disabled={true}>
                <option>Cargando lista...</option>
              </select>
            ) : (
              <select
                className='Instructor__ChangeGroup__Reports__Filters__List'
                value={selectedGroupToId}
                onChange={(e) => handleSelectGroupTo(e.target.value)}
                disabled={isWorking || groupsTo.length === 0}
              >
                <option value={0}>Selecciona el grupo de destino</option>
                {groupsTo.map(g => (
                  <option key={g.idGroup} value={g.idGroup}>
                    {g.groupName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Buscador */}
          <Search
            onChange={setSearchText}
            extraClassName={'Instructor__Reports__Ztudents__Search mt-5 mw-100'}
          />


          {/* Tabla con listado de Ztudiantes del grupo */}
          <section className='Tables__Container Instructor__Progress__ContainerTable Tables--Shadow Tables--Background'>
            {/* Botón de acción masiva + franja de seleccionados (solo si hay selección) */}
            {selectedIds.length > 0 && (
                <div className='container Instructor__SpaceUp'>
                    {/* Botón de acción masiva */}
                    <div className='Tables__ExtraButtons'>
                        <button
                        className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                        onClick={HandleMassiveMoveZtudents}
                        disabled={isWorking}
                        >
                        Mover {selectedIds.length} Ztudiante(s) ahora
                        </button>
                    </div>

                    {/* Franja con conteo de seleccionados + botón Cerrar */}
                    <div className='Tables__Table__RegistersSelected'>
                        {/* Botón pequeño (vista mobile) */}
                        <button
                        className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                        onClick={clearSelections}
                        >
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                        </button>

                        {/* Texto con cantidad seleccionada */}
                        <div className='Tables__Table__RegistersSelected__Text'>
                        {selectedIds.length} ztudiante(s) seleccionada(s)
                        </div>

                        {/* Botón grande (vista desktop) */}
                        <button
                        className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                        onClick={clearSelections}
                        >
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                        </button>
                    </div>
                </div>
            )}

            <table className='Tables__Table Instructor__Progress__ContainerTable__Table'>
              <thead>
                <tr className='Tables__Table__Row--Header'>
                  {/* Checkbox para selección masiva por página/filtrado */}
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Progress__ContainerTable__Table__InputColumn'>
                    <input
                      type='checkbox'
                      className='Tables__Table__InputColumn__Input'
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? processedRows.map(row => row.idZtudiante)
                          : []
                        setSelectedIds(newIds)
                      }}
                      checked={
                        processedRows.length > 0 &&
                        processedRows.every(row => selectedIds.includes(row.idZtudiante))
                      }
                    />
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Nombre(s)
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Apellido(s)
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Usuario
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Progress__ContainerTable__Table__ActionButtonsColumn'></td>
                </tr>
              </thead>
              <tbody>
                {processedRows?.length ? (
                  processedRows.map((r, idx) => (
                    <tr key={r.idZtudiante ?? idx} className='Tables__Table__Row'>
                      <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Progress__ContainerTable__Table__InputColumn'>
                        <input
                          type='checkbox'
                          className='Tables__Table__InputColumn__Input'
                          checked={isRowSelected(r.idZtudiante)}
                          onChange={() => toggleRowSelect(r.idZtudiante)}
                        />
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {`${r.name ?? ''}`.trim()}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {`${r.lastName ?? ''}`.trim()}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {r.user}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Progress__ContainerTable__Table__ActionButtonsColumn'>
                        <div className='Instructor__Progress__ContainerTable__Table__ActionButtonsColumn__Buttons'>
                          <button
                            type='button'
                            className='Btn__Ztudia--PrimaryOutline Instructor__Progress__ContainerTable__Table__ActionButtonsColumn__Buttons__Button'
                            onClick={() => ShowConfirmMoveModal(r)}
                            // Solo se bloquea si hay operación en curso: el mensaje de "selecciona grupo destino" lo maneja ShowConfirmMoveModal
                            disabled={isWorking}
                          >
                            Mover ahora
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className='Tables__Table__Row'>
                    <td className='Tables__Table__Row__Cell' colSpan={5}>
                      {isWorking ? 'Cargando...' : 'Sin resultados por mostrar'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          {errorMessage && errorMessage !== '' && (
            <p className='Instructor__Message--Error'>{errorMessage}</p>
          )}

          {/* Modal de confirmación para acción individual */}
          {showConfirmationModal && ztudentConfirmDetails && actionTypeOfModal === actions.MOVE && (
            <Confirm
              message={(() => {
                const originGroup = groupsFrom.find(g => g.idGroup === selectedGroupFromId)
                const destGroup = groupsTo.find(g => g.idGroup === selectedGroupToId)
                return `¿Estás segur@ de mover al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user}) del grupo '${originGroup?.groupName ?? ''}' al grupo '${destGroup?.groupName ?? ''}'?`
              })()}
              actionToYesButton={MoveSingleZtudent}
              textYesButton={'Sí, mover Ztudiante'}
              textNoButton={'No'}
              setShowModal={setShowConfirmationModal}
              setConfirmData={setZtudentConfirmDetails}
            />
          )}

          {/* Modal de resumen para acción masiva */}
          {showMassiveSummaryModal && massiveSummary && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={true}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowMassiveSummaryModal}
              setDetailsData={setMassiveSummary}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>
                  Resumen de Ztudiantes movidos de grupo
                </p>

                {massiveSummary ? (
                  <>
                    {massiveSummary.successful.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-check-circle'></i> Ztudiantes movidos:
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.successful.map((success, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {success.name} {success.lastName} ({success.user})
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.wrongs.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-x-circle'></i> Error al moverlo(s):
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.wrongs.map((wrong, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {wrong.name} {wrong.lastName} ({wrong.user}) – {wrong.error}
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.omitteds.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-skip-end-btn'></i> Omitidos:
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.omitteds.map((omitted, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {omitted.name} {omitted.lastName} ({omitted.user})
                              {omitted.reason && ` – ${omitted.reason}`}
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className='Modal__Layout--Summary__Text'>
                      No ha sido posible mostrar el resumen de lo realizado.
                    </p>
                  </>
                )}
              </div>
            </Details>
          )}
        </section>
      </InstructorBase>
    )
  )
}

export { ChangeGroup }